import { resolveDataDescription } from "../constants/testDataDescriptions";
import { TEST_NAME_MAP } from "../constants/testNameMap";

/** Matches report / detail UX for calibration row classification. */
const EXPIRATORY_FLOW_SENSOR_CALIBRATION_TEST_ID = 113029;

/**
 * Mirrors backend rule: PDB / power-supply aggregate test uses TT + 8 hex (error-only).
 */
export function is32BitTestId(test_id) {
  return Number(test_id) === 113007;
}

/**
 * @param {string} raw
 */
function normalizeCompound(raw) {
  if (typeof raw !== "string") return null;
  const s = raw.trim().replace(/^0x/i, "");
  if (s.length < 10) return null;
  return s;
}

/**
 * Find TT+DATA+ERROR or TT+ERROR bundle on the row (parity with REST body `error_code` string).
 * @param {object} row
 */
export function getCompoundErrorString(row) {
  if (typeof row.compound_error_code === "string") {
    return normalizeCompound(row.compound_error_code) ? row.compound_error_code.trim() : null;
  }
  if (typeof row.errorcode_raw === "string") {
    return normalizeCompound(row.errorcode_raw) ? row.errorcode_raw.trim() : null;
  }
  if (typeof row.errorcode === "string" && normalizeCompound(row.errorcode)) {
    return row.errorcode.trim();
  }
  const rd = row.raw_data;
  if (Array.isArray(rd) && rd.length === 1 && typeof rd[0] === "string") {
    const one = rd[0].trim();
    if (normalizeCompound(one.replace(/^0x/i, ""))) return one;
  }
  return null;
}

/**
 * Same extraction as backend `decodePreUseErrorCode` (non-async).
 * @param {number} test_id
 * @param {string} error_code Compound string TT + remainder (hex substrings).
 */
export function extractFromCompoundString(test_id, error_code) {
  const raw = typeof error_code === "string" ? error_code.trim() : "";
  const bare = raw.replace(/^0x/i, "");
  if (bare.length < 10) {
    throw new Error("Invalid compound error_code string length");
  }

  const wide = is32BitTestId(test_id);

  /** @type {string | null} */
  let dataHex = null;
  let errorCodeHex = "";

  if (wide) {
    errorCodeHex = bare.substring(2, 10).toUpperCase();
    if (errorCodeHex.length !== 8 || !/^[0-9a-fA-F]+$/.test(errorCodeHex)) {
      throw new Error("Non-hex error substring (32-bit test)");
    }
  } else {
    dataHex = bare.substring(2, 6).toUpperCase();
    errorCodeHex = bare.substring(6, 10).toUpperCase();
    if (dataHex.length !== 4 || !/^[0-9a-fA-F]+$/.test(dataHex)) {
      throw new Error("Non-hex DATA substring (16-bit test)");
    }
    if (errorCodeHex.length !== 4 || !/^[0-9a-fA-F]+$/.test(errorCodeHex)) {
      throw new Error("Non-hex ERROR substring (16-bit test)");
    }
  }

  const errorCode = Number.parseInt(errorCodeHex, 16);
  if (!Number.isFinite(errorCode)) {
    throw new Error("Invalid numeric error substring");
  }

  const maxBit = wide ? 31 : 15;
  const maskedErrorCode = wide ? errorCode >>> 0 : errorCode & 0x0000ffff;

  /** @type {number | null} */
  let data_value = null;
  let data_description = null;

  if (!wide && dataHex) {
    data_value = Number.parseInt(dataHex, 16);
    data_description = resolveDataDescription(test_id, data_value);

    /** Default label when catalogue does not define a template */
    if (data_description == null && Number.isFinite(data_value)) {
      data_description = `Measured data raw: ${data_value} (0x${dataHex.toUpperCase()})`;
    }
  }

  return {
    dataHex,
    errorCodeHex,
    errorCodeInt: errorCode,
    maskedErrorCode,
    maxBit,
    data_value,
    data_description,
  };
}

/**
 * When only numeric `errorcode` is known (MQTT/JSON compact row).
 */
export function extractFromNumericError(test_id, errorcodeNum) {
  const wide = is32BitTestId(test_id);
  const n = Number(errorcodeNum) || 0;
  const errorCodeHex = wide
    ? (n >>> 0).toString(16).toUpperCase().padStart(8, "0")
    : (n & 0xffff).toString(16).toUpperCase().padStart(4, "0");

  const maxBit = wide ? 31 : 15;
  const maskedErrorCode = wide ? n >>> 0 : n & 0x0000ffff;

  return {
    dataHex: null,
    errorCodeHex,
    errorCodeInt: maskedErrorCode,
    maskedErrorCode,
    maxBit,
    data_value: null,
    data_description: null,
  };
}

/**
 * Full decode identical to backend flow (compound-preferring).
 * @param {object} row Decoded diagnostic row `{ testid, errorcode?, ... }`
 */
export function decodePreUseErrorParts(row) {
  const test_id = Number(row.testid);
  const compound = getCompoundErrorString(row);

  try {
    if (compound) {
      return {
        ...extractFromCompoundString(test_id, compound),
        source: "compound",
      };
    }
  } catch {
    /** fall through to numeric */
  }

  return {
    ...extractFromNumericError(test_id, row.errorcode),
    source: "numeric",
  };
}

/** @typedef {{ bit_position: number, troubleshoot: string, action: string, service_action: string, test_description?: string }} DefRow */

/**
 * Bit scan — same predicate as backend.
 * @param {DefRow[]} definitions sorted by bit ASC
 * @param {number} maskedErrorCode
 * @param {number} maxBit
 */
export function decodeErrorBits(definitions, maskedErrorCode, maxBit = 31) {
  /** @type {DefRow[]} */
  const decoded_errors = [];
  for (const def of definitions) {
    const bit = def.bit_position;
    if (bit <= maxBit && ((maskedErrorCode >>> bit) & 1)) {
      decoded_errors.push({
        bit_position: bit,
        test_description: def.test_description,
        troubleshoot: def.troubleshoot,
        action: def.action,
        service_action: def.service_action,
      });
    }
  }
  return decoded_errors;
}

/**
 * Mirrors backend JSON response shape (`decodePreUseErrorCode`).
 * Feed catalogue `errors` rows + `test_description` from definition.
 *
 * @param {object} row
 * @param {{ test_id: number, test_name: string, test_description: string, errors: DefRow[], error_code_width?: number }} catalogDef Effective catalogue entry (merged/fallback OK).
 */
export function decodePreUseErrorCodePayload(row, catalogDef) {
  const extracted = decodePreUseErrorParts(row);

  const widthBits = Number(catalogDef.error_code_width) || (is32BitTestId(catalogDef.test_id) ? 32 : 16);
  const backendMaxBit = is32BitTestId(catalogDef.test_id) ? 31 : 15;
  const catalogMaxBit = Math.min(widthBits - 1, backendMaxBit);
  /** @type {number} */
  const catalogueMask =
    widthBits >= 32 ? 0xffffffff >>> 0 : (((1 << widthBits) >>> 0) - 1) >>> 0;
  const maskedForCatalog = (extracted.maskedErrorCode >>> 0) & catalogueMask;

  const defsSorted = [...catalogDef.errors].sort((a, b) => a.bit_position - b.bit_position);

  const enrichedDefs = defsSorted.map((d) => ({
    ...d,
    test_description: catalogDef.test_description,
  }));

  const decoded_errors = decodeErrorBits(enrichedDefs, maskedForCatalog, catalogMaxBit);

  return {
    success: true,
    test_id: catalogDef.test_id,
    test_name: catalogDef.test_name,
    data: extracted.dataHex,
    data_value: extracted.data_value,
    data_description: extracted.data_description,
    error_code: extracted.errorCodeHex,
    masked_error_code: maskedForCatalog,
    decode_source: extracted.source,
    decoded_errors,
  };
}

/** @param {object} row */
export function isExpiratoryFlowSensorCalibrationRow(row) {
  const tid = Number(row.testid);
  const nid = row.name_id != null ? Number(row.name_id) : NaN;
  if (tid === EXPIRATORY_FLOW_SENSOR_CALIBRATION_TEST_ID) return true;
  if (Number.isFinite(nid) && nid === EXPIRATORY_FLOW_SENSOR_CALIBRATION_TEST_ID) return true;
  const id = row.name_id ?? row.testid;
  const label = String(TEST_NAME_MAP[id] ?? `TEST_${id}`).toUpperCase().replace(/\s+/g, " ");
  return label.includes("EXPIRATORY FLOW SENSOR CALIBRATION");
}

/**
 * Single scalar for UX lines (compound TT+DATA+ERROR DATA field when present,
 * otherwise masked numeric `errorcode`).
 * @param {object} row
 */
export function getExpiratoryCalibrationScalarFromRow(row) {
  const parts = decodePreUseErrorParts(row);
  if (parts.data_value != null && Number.isFinite(parts.data_value)) return parts.data_value;
  return Number(row.errorcode) >>> 0;
}
